// eslint-disable-next-line import/no-cycle
import {
  ActionTypeEnum, FacebookMessengerButton, FieldConfig, ResponsiveDisplayStackEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import { getSectionIdsFromIframe, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import createTimedLRUCache from '@/helpers/createTimedLRUCache';
// eslint-disable-next-line import/no-cycle
import { LINE_HEIGHT_POSSIBLE_VALUES } from '@/components/template-builder/utils/constants';
// eslint-disable-next-line import/no-cycle
import { getSectionParent, getTemplateIframeDocument, isEmailTemplate } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { getDynamicStylesForActiveSection, getDynamicStylesForSelector } from '../utils/parser';
// eslint-disable-next-line import/no-cycle
import { sanitizeTranslationMarkup } from '../utils/translate';

// eslint-disable-next-line import/no-cycle
export * from './social-networks';
// eslint-disable-next-line import/no-cycle
export * from './link';
// eslint-disable-next-line import/no-cycle
export * from './smart-product-list';
// eslint-disable-next-line import/no-cycle
export * from './form';

export const getAttributeByName = (selector: string, attribute: string): string|undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    return element.getAttribute(attribute) ?? undefined;
  }
  return undefined;
};

export const getDecodedDataRedirect = (selector: string, attribute: string): string => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    const value = element.getAttribute(attribute) ?? '';
    let returnValue;
    try {
      returnValue = atob(value.replace('base64:', '').replace('#redirect_', '') ?? '');
    } catch (err) {
      returnValue = value;
    }
    return returnValue;
  }
  return '';
};

const cssPropertyCache = createTimedLRUCache<string, any>({
  timeout: 1500,
  maxSize: 500,
});

export const getCssPropertyByName = (selector: string, name: string, onlyValue = true, mobile = false): { type: string; value: any } | string | undefined => {
  const cacheKey = `${selector}|${name}|${onlyValue ? '1' : '0'}|${mobile === true ? 'mobile' : 'desktop'}`;
  const cached = cssPropertyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const dynamicStyles = getDynamicStylesForSelector(selector, mobile);
  const element = getTemplateIframeDocument().querySelectorAll(selector)[0];
  let returnValue;
  let value;
  if (Object.prototype.hasOwnProperty.call(dynamicStyles, name) && dynamicStyles[name]) {
    value = dynamicStyles[name];
  } else if (element) {
    if (name === 'background' && Object.prototype.hasOwnProperty.call(dynamicStyles, 'background-color')
      && dynamicStyles['background-color'] && Object.prototype.hasOwnProperty.call(dynamicStyles, 'background-image') && !dynamicStyles['background-image'].match('gradient')) {
      // eslint-disable-next-line no-param-reassign
      name = 'background-color';
      value = dynamicStyles['background-color'];
    } else {
      const css = window.getComputedStyle(element);
      value = css.getPropertyValue(name);
      if (mobile === true) {
        value = dynamicStyles[name] || value;
      }
    }
  }

  if (element && name === 'max-width' && element.tagName === 'IMG') {
    const currentWidth = (isEmailTemplate(TemplateEditorState.template?.type) ? dynamicStyles.width : dynamicStyles['max-width']);
    if ((new RegExp('%$')).test(currentWidth)) {
      value = currentWidth;
    } else if (element.tagName === 'IMG' && value && value.match('px$')) {
      const currentImgWidthAttr = (isEmailTemplate(TemplateEditorState.template?.type) ? element.getAttribute('width') : currentWidth);
      if (currentImgWidthAttr && parseInt(currentImgWidthAttr, 10) < parseInt(value, 10)) {
        value = `${currentImgWidthAttr}px`;
      }
    }
  } else if (element && value && name === 'line-height') {
    // Try to get the font-size from dynamic styles first
    let fontSizeValue = dynamicStyles['font-size'];
    const allowedValues = LINE_HEIGHT_POSSIBLE_VALUES.map((v) => v.value); // Allowed normalized line-height values

    // Fallback to computed font-size if dynamic one is missing
    if (!fontSizeValue) {
      const css = window.getComputedStyle(element);
      fontSizeValue = css.getPropertyValue('font-size');
    }

    const fontSize = parseFloat(fontSizeValue);

    // Convert percentage line-height to multiplier
    if (value && /%$/.test(value)) {
      const lineHeightPercent = parseFloat(value);
      value = (lineHeightPercent / 100).toFixed(2);
      // eslint-disable-next-line brace-style
    } else if (value && /px$/.test(value)) {
      const lineHeightPx = parseFloat(value);
      if (fontSize > 0) {
        value = (lineHeightPx / fontSize).toFixed(2);
      } else {
        value = '1'; // fallback if font-size is invalid
      }
    }

    // Normalize to closest allowed line-height multiplier
    if (name === 'line-height' && value) {
      const numericValue = parseFloat(value);
      value = allowedValues.reduce((prev, curr) => (Math.abs(parseFloat(curr) - numericValue) < Math.abs(parseFloat(prev) - numericValue) ? curr : prev));
    }
  }

  // Avoid return false value of background color (default is rgba(0, 0, 0, 0) is no background color set)
  if (value && name === 'background-color' && value === 'rgba(0, 0, 0, 0)') {
    value = '';
  }
  if (value) {
    returnValue = onlyValue ? value : { type: dynamicStyles[name] ? 'element' : 'parent', value };
  }

  // Define the CSS properties that need special handling for comma separation
  const backgroundProps = ['background-repeat', 'background-position-x', 'background-position-y', 'background-size'];
  const isBackgroundProp = backgroundProps.includes(name);
  const isBackground = name === 'background';

  if (returnValue) {
    // Utility function to remove everything after the first comma
    const replaceComma = (value2: string) => value2.replace(/,.*$/, '');

    // Utility function to extract gradient information
    const handleGradient = (value2: string) => value2.replace(/.*((linear|radial)-gradient.*\))$/, '$1');

    if (typeof returnValue === 'string') {
      if (isBackgroundProp) {
        // Remove everything after the first comma for specific background properties
        returnValue = replaceComma(returnValue);
      } else if (isBackground) {
        if (returnValue.match(/(linear|radial)-gradient/)) {
          // Extract gradient information for background property
          returnValue = handleGradient(returnValue);
        } else {
          // Replace with background-color if no gradient is found
          const returnValuString = getCssPropertyByName(selector, 'background-color', true, mobile);
          if (typeof returnValuString === 'string') {
            returnValue = returnValuString;
          }
        }
      }
    } else if (typeof returnValue === 'object' && typeof returnValue.value === 'string') {
      if (isBackgroundProp) {
        // Remove everything after the first comma for specific background properties
        returnValue.value = replaceComma(returnValue.value);
      } else if (isBackground) {
        if (returnValue.value.match(/(linear|radial)-gradient/)) {
          // Extract gradient information for background property
          returnValue.value = handleGradient(returnValue.value);
        } else {
          // Replace with background-color if no gradient is found
          const returnValuString = getCssPropertyByName(selector, 'background-color', true, mobile);
          if (typeof returnValuString === 'string') {
            returnValue.value = returnValuString;
          }
        }
      }
    }
  }
  cssPropertyCache.set(cacheKey, returnValue);
  return returnValue;
};

export const getBackgroundCssProperty = (selector: string, name: string, onlyValue = true): { type: string; value: any } | string | undefined => {
  let returnValue;
  returnValue = getCssPropertyByName(selector, name, onlyValue);
  if (typeof returnValue === 'string') {
    if (returnValue.match(/(linear|radial)-gradient/)) {
      returnValue = returnValue.replace(/.*((linear|radial)-gradient.*\))$/, '$1');
    } else {
      returnValue = getCssPropertyByName(selector, 'background-color', true);
    }
    if (typeof returnValue === 'string') {
      returnValue = returnValue.replace('!important', '').trim();
    }
  }
  return returnValue;
};

export const getContentWithLineBreaks = (selector: string): string | undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);

  if (element) {
    // Récupérer et nettoyer le contenu sans retraiter les balises <br>
    const sanitizedContent = sanitizeTranslationMarkup(element).innerHTML;
    // Remplacer uniquement les <br> par \n une seule fois
    return sanitizedContent.replace(/\n/gi, '').replace(/<br\s*\/?>/gi, '\n');
  }
  return undefined;
};

export const getContent = (selector: string) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  if (element) {
    // This getter can be involved by translation
    return sanitizeTranslationMarkup(element).innerHTML;
  }
  return undefined;
};

export const getClassByName = (selector: string, name: string): string|undefined => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    const classes = element.getAttribute('class')?.split(' ');
    const currentClass = classes?.filter((className: string) => className.startsWith(name))[0];
    return currentClass?.replace(name, '');
  }
  return undefined;
};

export const hasClass = (selector: string, name: string, options: any = null): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  const returnValue = element?.classList.contains(name) ?? false;
  return typeof options === 'object' && options !== null && options.revert === true ? !returnValue : returnValue;
};

export const hasClassElementAndChild = (selector: string, name: string): boolean => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  const resolvedSelector = selector.includes(',')
    ? selector
      .split(',') // Séparer les sélecteurs s'il y a une virgule
      .map((sel) => `${sel.trim()} .${name}`) // Ajouter `.name` à chaque sélecteur
      .join(',') // Reconstituer le sélecteur
    : `${selector} .${name}`; // Sinon, ajouter directement `.name`
  const childElement: HTMLElement | null = template?.querySelector(resolvedSelector);
  return (element?.classList.contains(name) || childElement !== null) ?? false;
};

export const getTagType = (selector: string): string|null => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    return element.tagName.toLowerCase();
  }
  return null;
};

export const getFacebookButtons = (selector: string): Array<object> => {
  const template = getTemplateIframeDocument();
  const buttonsContainer: HTMLElement|null = template?.querySelector(selector);
  const buttonsConfiguration: Array<object> = [];

  if (buttonsContainer) {
    const buttons: Array<HTMLElement> = Array.from(buttonsContainer.querySelectorAll('.button'));

    buttons.forEach((button: HTMLElement) => {
      const dataRedirect = button.getAttribute('data-redirect') ?? '';
      const text = sanitizeTranslationMarkup(button).innerHTML;

      const newButton: FacebookMessengerButton = {
        action: dataRedirect.match(new RegExp('#redirect_')) ? ActionTypeEnum.DISPLAY_TEMPLATE : ActionTypeEnum.REDIRECT,
        dataRedirect: dataRedirect.match(new RegExp('(#redirect_)|(base64:)'))
          ? atob(dataRedirect.replace('base64:', '').replace('#redirect_', ''))
          : dataRedirect,
        text,
        selectedTemplate: null,
      };

      buttonsConfiguration.push(newButton);
    });
  }

  return buttonsConfiguration;
};

export const isVisible = (items: Array<FieldConfig>, targetId: string) => {
  const targetField = items.find((item) => item.fieldId === targetId);
  if (targetField) {
    const option = targetField.properties[0].value !== undefined ? targetField.properties[0].value.toString() : Object.values(targetField.options).pop()?.defaultValue ?? '';
    return option.toString() === targetField.enableValue;
  }
  return false;
};

export const getBackgroundImageProperties = (selector: string, name: string): { type: string; value: any } | string | undefined => {
  // We check if selector is dedicated to a widget or not
  const idWidget = selector.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  let currentSelector = selector;

  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId);
      if (dynamicStyles) {
        const { sheet } = dynamicStyles;
        if (sheet) {
          Object.values(sheet.cssRules).forEach((styleRule: any) => {
            if (
              styleRule.selectorText.match(new RegExp(selector))
              && styleRule.style['backgroundImage' as any]
            ) {
              currentSelector = styleRule.selectorText;
            }
          });
        }
      }
    }
  });

  return currentSelector;
};

export const getBackgroundImageValue = (selector: string, name: string): { type: string; value: any } | string | undefined => {
  // We check if selector is dedicated to a widget or not
  const idWidget = selector.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  let value = '';

  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId);
      if (dynamicStyles) {
        const { sheet } = dynamicStyles;
        if (sheet) {
          Object.values(sheet.cssRules).forEach((styleRule: any) => {
            if (
              typeof styleRule.selectorText === 'string'
              && styleRule.selectorText.match(new RegExp(selector))
              && styleRule.style['backgroundImage' as any]
            ) {
              value = styleRule.style.backgroundImage;
            }
          });
        }
      }
    }
  });

  return value;
};

export const isMediaDisplayAlreadyRestricted = (selector: string) => {
  const template = getTemplateIframeDocument();
  let isMediaRestricted = false;
  if (template) {
    const element = template.querySelector(selector);
    if (element) {
      const widgets = Array.from(element.querySelectorAll('[data-widgettype]'));
      widgets.forEach((widget) => {
        if (Array.from(widget.classList).some((cls) => cls.startsWith('show-for-'))) {
          isMediaRestricted = true;
        }
      });
      if (isMediaRestricted) {
        return 'templateBuilder.fields.warningMessages.mobileDisplayAlreadyRestrictedFromWidgets';
      }

      const sectionParent = getSectionParent(element as HTMLElement);
      if (sectionParent && Array.from(sectionParent.classList).some((cls) => cls.startsWith('show-for-'))) {
        return 'templateBuilder.fields.warningMessages.mobileDisplayAlreadyRestrictedFromSections';
      }
    }
  }
  return isMediaRestricted;
};

export const getResponsiveDisplay = (selector: string, name: string): string => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);

  const resolvedSelector = selector.includes(',')
    ? selector
      .split(',') // Séparer les sélecteurs s'il y a une virgule
      .map((sel) => `${sel.trim()} .${name}`) // Ajouter `.name` à chaque sélecteur
      .join(',') // Reconstituer le sélecteur
    : `${selector} .${name}`; // Sinon, ajouter directement `.name`
  const childElement: HTMLElement | null = template?.querySelector(resolvedSelector);
  const isResponsive = (element?.classList.contains(name) || childElement !== null) ?? false;
  if (!isResponsive) {
    return ResponsiveDisplayStackEnum.NO_STACK;
  }
  const column = element?.querySelector('.columns');
  if (column && column.getAttribute('dir') === ResponsiveDisplayStackEnum.RTL) {
    return ResponsiveDisplayStackEnum.RTL;
  }
  return ResponsiveDisplayStackEnum.LTR;
};
